/**
 * Questionnaires Page - Unified view with Templates and Supplier Responses tabs
 *
 * This page combines:
 * - Tab 1: Templates - Admin configuration for questionnaire templates
 * - Tab 2: Supplier Responses - Operational tracking of supplier questionnaire completion
 *
 * Addresses client requirement to have both template management AND operational
 * supplier response tracking accessible from the Questionnaires menu.
 *
 * Both of those are the CUSTOMER's jobs, and the page had no role awareness, so
 * a supplier saw the customer's page verbatim: a "Templates" tab for authoring
 * questionnaires they cannot author (the API correctly returns none, so it was
 * an always-empty tab), and their own submissions labelled "Supplier Responses"
 * as though they were tracking someone else. Suppliers now get the one tab that
 * means something to them, named from their side.
 */
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ClipboardList, FileText, Users } from 'lucide-react';
import { useIsSupplier } from '../hooks/usePortalMode';

// Import the existing components as tab content
import QualificationTemplates from './QualificationTemplates';
import SupplierQualifications from './SupplierQualifications';

export default function Questionnaires() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSupplier = useIsSupplier();
  const activeTab = searchParams.get('tab') || (isSupplier ? 'responses' : 'templates');

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="px-6 pt-6">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Questionnaires</h1>
        </div>
        <p className="text-muted-foreground">
          {isSupplier
            ? 'Questionnaires your customers have asked you to complete'
            : 'Manage questionnaire templates and track supplier completion status'}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-6 border-b">
          <TabsList className="h-12 bg-transparent p-0 gap-4">
            {/* Customers build templates before they chase responses, so
                Templates leads for them. Suppliers have no Templates tab at
                all, so their single tab stays first either way. */}
            {!isSupplier && (
              <TabsTrigger
                value="templates"
                className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Templates
              </TabsTrigger>
            )}
            <TabsTrigger
              value="responses"
              className="relative h-12 px-4 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent hover:bg-muted/50"
            >
              <Users className="h-4 w-4 mr-2" />
              {isSupplier ? 'My Questionnaires' : 'Supplier Responses'}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Content */}
        {!isSupplier && (
          <TabsContent value="templates" className="mt-0">
            <QualificationTemplates />
          </TabsContent>
        )}

        <TabsContent value="responses" className="mt-0">
          <SupplierQualifications />
        </TabsContent>
      </Tabs>
    </div>
  );
}
